import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Skull, Smile } from "lucide-react";

import api from "../../api/axios";

const ResetPassword = () => {
    const navigate = useNavigate();
    const { token: routeToken } = useParams();

    const [token, setToken] = useState("");

    const [newPassword, setNewPassword] =
        useState("");

    const [confirmPassword, setConfirmPassword] =
        useState("");

    const [showPassword, setShowPassword] =
        useState(false);

    const [
        showConfirmPassword,
        setShowConfirmPassword,
    ] = useState(false);

    const [error, setError] = useState("");

    const [success, setSuccess] = useState("");

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const params =
            new URLSearchParams(
                window.location.search,
            );

        const hashParams =
            new URLSearchParams(
                window.location.hash.replace(/^#/, ""),
            );

        const pathToken =
            window.location.pathname
                .split("/")
                .filter(Boolean)[1];

        const t = (
            params.get("token") ||
            params.get("resetToken") ||
            hashParams.get("token") ||
            routeToken ||
            pathToken ||
            ""
        ).trim();

        if (!t) {
            setError(
                "Invalid reset link",
            );
        } else {
            setToken(t);
        }
    }, [routeToken]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");

        setSuccess("");

        const strongPassword =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

        if (
            !strongPassword.test(
                newPassword,
            )
        ) {
            return setError(
                "Password must contain uppercase, lowercase, number, special character and minimum 8 characters",
            );
        }

        if (
            newPassword !==
            confirmPassword
        ) {
            return setError(
                "Passwords do not match",
            );
        }

        try {
            setLoading(true);

            const res =
                await api.post(
                    "/api/employee/auth/reset-password",
                    {
                        token: token.trim(),
                        password: newPassword,
                        confirmPassword,
                    },
                );

            setSuccess(
                res.data.message,
            );

            setTimeout(() => {
                navigate("/login");
            }, 2500);
        } catch (err) {
            setError(
                err.response?.data
                    ?.message ||
                "Failed to reset password",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center px-4">

            <div className="w-full max-w-md bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8 shadow-2xl">

                {/* TITLE */}
                <h1 className="text-3xl font-bold text-white mb-2 text-center">
                    Reset Password
                </h1>

                <p className="text-gray-500 text-center mb-8">
                    Create your new password
                </p>

                {/* ERROR */}
                {error && (
                    <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3">
                        {error}
                    </div>
                )}

                {/* SUCCESS */}
                {success && (
                    <div className="mb-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm rounded-xl px-4 py-3">
                        {success}
                    </div>
                )}

                {!success && (
                    <form
                        onSubmit={
                            handleSubmit
                        }
                        className="space-y-5"
                    >

                        {/* NEW PASSWORD */}
                        <div>

                            <label className="block text-sm text-gray-400 mb-2">
                                New Password
                            </label>

                            <div className="relative">

                                <input
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    value={
                                        newPassword
                                    }
                                    onChange={(
                                        e,
                                    ) =>
                                        setNewPassword(
                                            e.target
                                                .value,
                                        )
                                    }
                                    placeholder="Enter new password"
                                    className="w-full px-4 py-3 pr-14 rounded-xl bg-[#111] border border-[#2a2a2a] text-white outline-none focus:border-emerald-500 transition"
                                    required
                                />

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(
                                            !showPassword,
                                        )
                                    }
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-emerald-400 transition"
                                >
                                    {showPassword ? (
                                        <Smile
                                            size={
                                                20
                                            }
                                        />
                                    ) : (
                                        <Skull
                                            size={
                                                20
                                            }
                                        />
                                    )}
                                </button>

                            </div>

                        </div>

                        {/* CONFIRM PASSWORD */}
                        <div>

                            <label className="block text-sm text-gray-400 mb-2">
                                Confirm Password
                            </label>

                            <div className="relative">

                                <input
                                    type={
                                        showConfirmPassword
                                            ? "text"
                                            : "password"
                                    }
                                    value={
                                        confirmPassword
                                    }
                                    onChange={(
                                        e,
                                    ) =>
                                        setConfirmPassword(
                                            e.target
                                                .value,
                                        )
                                    }
                                    placeholder="Confirm password"
                                    className="w-full px-4 py-3 pr-14 rounded-xl bg-[#111] border border-[#2a2a2a] text-white outline-none focus:border-emerald-500 transition"
                                    required
                                />

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowConfirmPassword(
                                            !showConfirmPassword,
                                        )
                                    }
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-emerald-400 transition"
                                >
                                    {showConfirmPassword ? (
                                        <Smile
                                            size={
                                                20
                                            }
                                        />
                                    ) : (
                                        <Skull
                                            size={
                                                20
                                            }
                                        />
                                    )}
                                </button>

                            </div>

                        </div>

                        {/* PASSWORD RULES */}
                        <div className="text-xs text-gray-500 leading-6 bg-[#111] border border-[#2a2a2a] rounded-xl p-4">

                            <p className="text-sm text-gray-300 mb-2 font-medium">
                                Password Policy & Requirements
                            </p>

                            <ul className="list-disc pl-5 mt-2 space-y-1">

                                <li className="text-emerald-400 font-semibold">
                                    Use a strong, unique password
                                </li>

                                <li>
                                    Minimum 8 characters
                                </li>

                                <li>
                                    One uppercase letter
                                </li>

                                <li>
                                    One lowercase letter
                                </li>

                                <li>
                                    One number
                                </li>

                                <li>
                                    One special character
                                </li>

                            </ul>

                        </div>

                        {/* BUTTON */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-emerald-500 hover:bg-emerald-600 transition text-black py-3 rounded-xl font-semibold disabled:opacity-60"
                        >
                            {loading
                                ? "Resetting..."
                                : "Reset Password"}
                        </button>

                    </form>
                )}

            </div>
        </div>
    );
};

export default ResetPassword;
